// ============================================================================
// "Show everyone" on the hours tab — remembered per user.
//
// ⛔ NO TABLE AND NO COLUMN. This mirrors `useFacilityScope`
// (src/shared/scope/use-facility-scope.ts) exactly: a localStorage key namespaced
// per employee id, so switching accounts on a shared machine does not inherit
// the other person's choice. `theme.tsx` says in its own comment that it
// deliberately copies the same shape. Three uses of one pattern, not a fourth
// invention.
//
// ⚠️ The prompt for this work named "the row-density setting" as the precedent
// to copy. There is no row-density setting anywhere in src/, and no preference
// table or column. This hook is the closest real precedent and is literally the
// same question — mine, or everything.
//
// This is a VIEW filter, not a security boundary. RLS permits an app employee to
// read the punch tables org-wide (Ben, 2026-09-21), exactly as useFacilityScope
// notes for facilities, so the toggle is for the person's benefit and nothing
// hangs on it.
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../shared/auth';

const STORAGE_PREFIX = 'tcs.timeHoursEveryone.';

function storageKey(employeeId: string | null | undefined): string | null {
  return employeeId ? `${STORAGE_PREFIX}${employeeId}` : null;
}

function readStored(employeeId: string | null | undefined): boolean | null {
  const key = storageKey(employeeId);
  if (!key) return null;
  try {
    const raw = window.localStorage.getItem(key);
    return raw === '1' ? true : raw === '0' ? false : null;
  } catch {
    return null;
  }
}

function writeStored(employeeId: string | null | undefined, everyone: boolean): void {
  const key = storageKey(employeeId);
  if (!key) return;
  try {
    window.localStorage.setItem(key, everyone ? '1' : '0');
  } catch {
    /* private browsing / quota — the choice just falls back to the default */
  }
}

export interface HoursScopeState {
  /** OFF by default (Ben, 2026-09-21) — your own buildings until you ask. */
  everyone: boolean;
  setEveryone: (next: boolean) => void;
}

export function useTimeHoursScope(): HoursScopeState {
  const { employee } = useAuth();
  const eid = employee?.employee_id ?? null;
  const [everyone, setState] = useState<boolean>(() => readStored(eid) ?? false);

  // Re-read when the signed-in person changes.
  useEffect(() => {
    setState(readStored(eid) ?? false);
  }, [eid]);

  const setEveryone = useCallback((next: boolean) => {
    setState(next);
    writeStored(eid, next);
  }, [eid]);

  return { everyone, setEveryone };
}
